<script>
import {
  templatePageMagasinEmits,
  templatePageMagasinProps,
  useTemplatePageMagasin
} from "./mixin";
import Map from "@/components/molecules/map";
import Card from "@/components/molecules/card";
import Titre from "@/components/atoms/titre";
import IconShipping from "@/components/molecules/icon-shipping";
import Divider from "@/components/atoms/divider";
import Button from "@/components/atoms/button";
import LinkTo from "@/components/atoms/link-to";
import ButtonIcon from "@/components/molecules/button-icon";
import Icon from "@/components/atoms/icon";
import Badge from "@/components/atoms/badge";
import footerCodes from "@/utils/footer-codes";
import HtmlParser from "@/components/organisms/cms-container/html-parser";

export default {
  name: "FicheMagasin",
  components: {
    Map,
    Card,
    Titre,
    IconShipping,
    Divider,
    Button,
    LinkTo,
    ButtonIcon,
    Icon,
    Badge,
    HtmlParser
  },
  props: {
    ...templatePageMagasinProps()
  },
  emits: ["openOrcloseStoreLocator", ...templatePageMagasinEmits()],
  setup(props, context) {
    const {
      ShopPageCodes,
      maxServices,
      itineraire,
      exceptionalOpening,
      exceptionalClosure,
      shopTimetable,
      positions,
      hasMagasin,
      horairesMagasin,
      isOpen,
      changeShop,
      displayAllServices,
      getPushData
    } = useTemplatePageMagasin(props, context);

    return {
      ShopPageCodes,
      maxServices,
      itineraire,
      exceptionalOpening,
      exceptionalClosure,
      shopTimetable,
      positions,
      hasMagasin,
      horairesMagasin,
      isOpen,
      changeShop,
      displayAllServices,
      getPushData
    };
  },
  data() {
    return {
      footerCodes
    };
  }
};
</script>

<template>
  <div class="u-flex">
    <div
      class="u-sticky u-bgcolor-white u-p-sm u-flex-shrink-none"
      style="width: 34rem"
    >
      <Card class="u-mb-xs">
        <Map
          :positions="positions"
          :has-marker="false"
          style="height: 300px"
        />
      </Card>
      <Titre
        size="sm"
        tag="p"
        class="u-mb-xs"
      >
        Magasin {{ magasin.attributes.magasin }}
      </Titre>
      <p class="u-font-15">
        {{ newlineToSpace(magasin.attributes.adresse) }}
      </p>
      <p class="u-font-15">
        {{ magasin.attributes.cp }} {{ magasin.attributes.ville }}
      </p>

      <ButtonIcon
        link
        variant="primary"
        icon-name="map"
        icon-color="primary"
        :href="itineraire"
        target="__blank"
      >
        Itinéraire
      </ButtonIcon>

      <Divider
        dotted
        class="u-mt-md u-mb-md"
      />
      <template v-if="
          shopTimetable &&
            shopTimetable.timetable &&
            shopTimetable.timetable.length > 0
        ">
        <p class="u-font-15 u-font-500 u-mb-xxs">
          <strong>Horaires</strong>
        </p>
        <template
          v-for="(item, index) in shopTimetable.timetable"
          :key="`timetable-${index}`"
        >
          <div
            v-if="item.jour && (item.from || item.to)"
            class="u-flex u-flex-justify-between u-font-15"
            :class="
              index !== shopTimetable.timetable.length - 1
                ? 'u-mb-xxs'
                : 'u-mb-md'
            "
          >
            <p v-if="item.jour">
              {{ item.jour }}
            </p>
            <p v-if="item.from == '00:00' && item.to == '00:00'">
              Fermé
            </p>
            <p v-else-if="item.from && item.to">
              {{ item.from }} - {{ item.to }}
            </p>
            <p v-else-if="item.from">
              {{ item.from }}
            </p>
            <p v-else-if="item.to">
              {{ item.to }}
            </p>
          </div>
        </template>
      </template>
      <template v-if="exceptionalOpening && exceptionalOpening.length > 0">
        <p class="u-font-15 u-mb-xs">
          <strong class="u-font-500">Ouvertures exceptionnelles</strong>
        </p>
        <p
          v-for="(item, index) in exceptionalOpening"
          :key="item.type + index"
          class="u-color-success u-font-15 "
          :class="
            index !== exceptionalOpening.length - 1 ? 'u-mb-xxs' : 'u-mb-md'
          "
        >
          {{ uppercaseFirstLetter(item.date) }} de {{ item.from }} à
          {{ item.to }}
        </p>
      </template>
      <template v-if="exceptionalClosure && exceptionalClosure.length > 0">
        <p class="u-font-15 u-font-500 u-mb-xs">
          <strong class="u-font-500 ">Fermetures exceptionnelles</strong>
        </p>
        <p
          v-for="(item, index) in exceptionalClosure"
          :key="item.type + index"
          class="u-color-alert u-font-15 "
          :class="
            index !== exceptionalClosure.length - 1 ? 'u-mb-xxs' : 'u-mb-md'
          "
        >
          {{ uppercaseFirstLetter(item.date) }}
        </p>
      </template>
      <Divider
        v-if="
          (shopTimetable &&
            shopTimetable.timetable &&
            shopTimetable.timetable.length > 0) ||
            (exceptionalOpening && exceptionalOpening.length > 0) ||
            (exceptionalClosure && exceptionalClosure.length > 0)
        "
        dotted
        class="u-mt-md u-mb-md"
      />
      <template v-if="magasin.attributes.telephone_magasin">
        <p class="u-font-15 u-mb-xs">
          <strong class="u-font-500">Contactez votre magasin</strong>
        </p>
        <div
          v-if="magasin.attributes.telephone_magasin"
          class="u-flex u-flex-align-center"
        >
          <Icon name="phone" />
          <p
            class="u-ml-sm u-font-15"
            itemprop="telephone"
          >
            {{ magasin.attributes.telephone_magasin }}
          </p>
        </div>
        <div class="u-flex u-flex-align-center">
          <Icon name="email-fast" />
          <LinkTo
            class="u-ml-sm u-font-15"
            variant="primary"
            itemprop="email"
            to="/infos-et-services/aide/contactez-nous"
          >
            Contactez-nous
          </LinkTo>
        </div>

        <Divider
          dotted
          class="u-mt-md u-mb-md"
        />
      </template>
      <template v-if="
          magasin.attributes.services && magasin.attributes.services.length > 0
        ">
        <p class="u-font-15 u-mb-xs">
          <strong class="u-font-500">Les services en magasin</strong>
        </p>
        <template
          v-for="(service, index) in magasin.attributes.services"
          :key="service.libelle"
        >
          <p
            v-if="index < maxServices"
            class="u-font-15 u-mb-xs"
          >
            {{ service.libelle }}
          </p>
        </template>
        <ButtonIcon
          v-if="magasin.attributes.services.length > 2"
          :icon-name="
            maxServices == magasin.attributes.services.length
              ? 'arrow-up'
              : 'arrow-down'
          "
          link
          icon-size="sm"
          variant="text"
          @click="displayAllServices"
        >
          Voir
          <template v-if="maxServices === 2">
            tous les services
          </template>
          <template v-else>
            moins de services
          </template>
        </ButtonIcon>

        <Divider
          dotted
          class="u-mt-md u-mb-md"
        />
      </template>
      <template v-if="!context && hasBlog">
        <p class="u-font-15 u-mb-xs">
          <strong class="u-font-500">Le blog de votre magasin</strong>
        </p>
        <LinkTo
          variant="primary"
          size="sm"
          :to="`/magasins/${magasin.attributes.aliasWeb}-m-${magasin.id}/blog`"
        >
          Voir les actualités
        </LinkTo>
        <Divider
          dotted
          class="u-mt-md u-mb-md"
        />
      </template>

      <div class="u-flex u-flex-column u-mt-sm">
        <p class="u-color-text-light u-font-15 u-mb-xxs">
          Suivez-nous :
        </p>
        <div>
          <ButtonIcon
            v-if="magasin.attributes.facebook"
            icon-name="social-facebook-circle"
            icon-color="primary"
            icon-size="sm"
            class="u-mr-xxs"
            aria-label="Lien vers la page Facebook Cora."
            :href="magasin.attributes.facebook"
            target="__blank"
            title="Facebook"
          />
          <ButtonIcon
            icon-name="social-whatsapp-circle"
            icon-color="primary"
            icon-size="sm"
            class="u-mr-xxs"
            aria-label="Lien vers la page Whatsapp Cora."
            :href="footerCodes.WHATSAPP"
            target="__blank"
            title="Whatsapp"
          />
          <ButtonIcon
            v-if="magasin.attributes.instagram"
            icon-name="social-instagram-circle"
            class="u-mr-xxs"
            icon-color="primary"
            icon-size="sm"
            aria-label="Lien vers le compte Instagram Cora."
            :href="magasin.attributes.instagram"
            target="__blank"
            title="Instagram"
          />
          <ButtonIcon
            icon-name="social-tiktok-circle"
            class="u-mr-xxs"
            icon-color="primary"
            icon-size="sm"
            aria-label="Lien vers le compte tiktok Cora."
            :href="footerCodes.TIKTOK"
            target="__blank"
            title="TikTok"
          />
          <ButtonIcon
            icon-name="social-linkedin-circle"
            class="u-mr-xxs"
            icon-color="primary"
            icon-size="sm"
            aria-label="Lien vers le compte linkedin Cora."
            :href="footerCodes.LINKEDIN"
            target="__blank"
            title="LinkedIn"
          />
          <ButtonIcon
            v-if="magasin.attributes.twitter"
            icon-name="social-twitter-circle"
            icon-color="primary"
            icon-size="sm"
            class="u-mr-xxs"
            aria-label="Lien vers le compte Twitter Cora."
            :href="magasin.attributes.twitter"
            target="__blank"
            title="Twitter"
          />
          <ButtonIcon
            icon-name="social-pinterest-circle"
            icon-color="primary"
            icon-size="sm"
            aria-label="Lien vers le compte pinterest Cora."
            :href="footerCodes.PINTEREST"
            target="__blank"
            title="Pinterest"
          />
          <ButtonIcon
            v-if="magasin.attributes.youtube"
            icon-name="social-youtube-circle"
            class="u-mr-xxs"
            icon-color="primary"
            icon-size="sm"
            aria-label="Lien vers le compte Youtube Cora."
            :href="magasin.attributes.youtube"
            target="__blank"
            title="Youtube"
          />
        </div>
      </div>
    </div>

    <div class="u-ml-lg u-mr-lg u-flex-grow u-full-width-ie">
      <div class="u-mt-sm u-mb-sm u-flex u-flex-align-center u-color-grey-2 u-font-12">
        <p class="u-mr-xs">
          Les magasins
        </p>
        <Icon
          name="arrow-right"
          size="xxs"
          variant="grey-2"
        />
        <LinkTo
          v-if="context"
          :to="`/magasins/${magasin.attributes.aliasWeb}-m-${magasin.id}`"
        >
          Magasin {{ magasin.attributes.magasin }}
        </LinkTo>
        <p
          v-else
          class="u-mr-xs"
        >
          <strong>Magasin {{ magasin.attributes.magasin }}</strong>
        </p>
        <template v-if="context">
          <Icon
            name="arrow-right"
            size="xxs"
            variant="grey-2"
          />
          <p v-if="context === 'hp-blog'">
            <strong> le blog de votre magasin</strong>
          </p>
          <LinkTo
            v-else
            :to="
              `/magasins/${magasin.attributes.aliasWeb}-m-${magasin.id}/blog`
            "
          >
            <p>le blog de votre magasin</p>
          </LinkTo>
        </template>
        <template v-if="context === 'page-article-blog' && article">
          <Icon
            name="arrow-right"
            size="xxs"
            variant="grey-2"
          />
          <p class="u-ml-xs">
            <strong>{{ article.attributes.titre }}</strong>
          </p>
        </template>
      </div>

      <Card
        radius="md"
        class="u-px-md u-pt-md u-pt-md u-pb-lg25"
      >
        <div class="u-flex u-flex-align-center u-flex-justify-between">
          <div class="u-flex u-flex-align-center u-mb-xs">
            <Titre
              tag="h1"
              size="lg"
              class="u-mr-md"
            >
              Cora hypermarché {{ magasin.attributes.magasin }} devient Carrefour {{ magasin.attributes.magasin }}
            </Titre>
            <template v-if="hasMagasin && context!=='page-article-blog' && context!=='hp-blog'">
              <Button
                variant="primary"
                radius="full"
                size="sm"
                @click="changeShop(magasin.id)"
              >
                Choisir ce magasin
              </Button>
            </template>
            <template v-else-if="context!=='page-article-blog' && context!=='hp-blog'">
              <Badge
                variant="success"
                circle
                size="xs"
              >
                <Icon
                  name="check"
                  variant="white"
                  size="xxs"
                />
              </Badge>
              <p class="u-font-13 u-ml-xs u-mr-lg">
                Vous avez sélectionné ce magasin
              </p>
            </template>
          </div>
          <template v-if="!hasMagasin && context!=='page-article-blog' && context!=='hp-blog'">
            <div class="u-mr-lg">
              <Button
                link
                variant="primary"
                @click="$emit('openOrcloseStoreLocator')"
              >
                Choisir un autre magasin
              </Button>
            </div>
          </template>
        </div>

        <p
          v-if="isOpen"
          class="u-color-success u-font-17 u-mb-xs"
        >
          {{ horairesMagasin }}
        </p>
        <p
          v-else
          class="u-color-alert u-font-17 u-mb-xs"
        >
          Actuellement fermé
        </p>
        <template v-if="!isPro">
          <IconShipping
            v-if="
              magasin.attributes.dispoPremierCreneau &&
                magasin.attributes.dispoPremierCreneau !== 'indisponible'
            "
            type="DRIVE"
            variant="primary"
            class="u-font-17 u-mb-md"
          >
            Prochain créneau drive :&nbsp;<strong class="u-font-500">{{
              magasin.attributes.dispoPremierCreneau
            }}</strong>
          </IconShipping>
          <IconShipping
            v-else
            type="DRIVE"
            variant="primary"
            class="u-font-17 u-mb-md"
          >
            Aucun créneau drive n'est disponible pour le moment dans ce magasin.
          </IconShipping>
          <HtmlParser
            v-if="getPushData(ShopPageCodes.FICHE_MAGASIN_SEO_FOOTER).html_content"
            class="u-pb-sm"
            :context="`FICHE MAGASIN SEO FOOTER`"
            :content="getPushData(ShopPageCodes.FICHE_MAGASIN_SEO_FOOTER).html_content"
          />
          <Divider
            dotted
            class="u-mt-sm u-mb-lg"
          />
        </template>
        <slot />
      </Card>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-prix-carburant-container {
  max-width: 90rem;
  margin-left: auto;
  margin-right: auto;
}
</style>
